// OpenAI's ChatGPT Ads measurement pixel ("oaiq"). initOpenAIPixel() runs only from
// loadMarketingScripts() in App.tsx, so it inherits the marketing-consent gate; until it has run
// window.oaiq does not exist and trackOpenAI() is a no-op. The pixel ID is public, like the others.
const PIXEL_ID = "GFWTcnPBFWTaHMYnzHvoFa";
const CLICK_KEY = "oaiq_oppref";
const THIRTY_DAYS_S = 30 * 24 * 60 * 60;

type Oaiq = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
	interface Window {
		oaiq?: Oaiq;
	}
}

// An ad click arrives with OpenAI's click id as ?oppref=. Marketing pages never load the pixel (no App,
// no consent banner) and their CTAs are plain links that drop the query string, so the id is kept for
// this tab only, sent nowhere, until initOpenAIPixel() hands it over after consent.
export function rememberOpenAIClick() {
	try {
		const id = new URLSearchParams(window.location.search).get("oppref");
		if (id) sessionStorage.setItem(CLICK_KEY, id);
	} catch {
		// storage blocked: attribution is best-effort
	}
}

// The SDK reads the click id once at init: from ?oppref=, else from its own __oppref cookie. Writing
// that cookie in the SDK's format (30 days, registrable domain) is what it would have done itself.
// shortcut: depends on oaiq 0.1.41's internal cookie name, check it when OpenAI ships a new SDK.
function handOverRememberedClick() {
	try {
		const id = sessionStorage.getItem(CLICK_KEY);
		if (!id) return;
		document.cookie = `__oppref=${encodeURIComponent(id)}; Path=/; Max-Age=${THIRTY_DAYS_S}; SameSite=Lax; Domain=stippl.io; Secure`;
		sessionStorage.removeItem(CLICK_KEY);
	} catch {
		// storage blocked: attribution is best-effort
	}
}

export function initOpenAIPixel() {
	if (window.oaiq) return;
	handOverRememberedClick();
	// OpenAI's loader snippet: queue calls until oaiq.min.js arrives, which replays them.
	const queue: Oaiq = (...args) => {
		queue.q?.push(args);
	};
	queue.q = [];
	window.oaiq = queue;
	const script = document.createElement("script");
	script.async = true;
	script.src = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
	document.head.appendChild(script);
	window.oaiq("init", { pixelId: PIXEL_ID });
	window.oaiq("measure", "page_viewed", { type: "contents" });
}

// Who the pixel currently has user data for. The hash is async, so trackOpenAI() awaits `identity`
// rather than racing it, and `identityArrived` wakes a trackOpenAI() that got here before App's auth effect.
let identifiedUid: string | undefined;
let identity: Promise<void> | undefined;
let identityArrived: (() => void) | undefined;
const IDENTITY_WAIT_MS = 2000;

async function sha256Hex(value: string) {
	const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
	return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

// Hashed user matching: lets OpenAI tie a signup to an ad click when the oppref id is lost, which is
// common because most clicks start in the ChatGPT apps and finish in another browser or device. The
// SDK wants user data on init (request-scoped, never on measure), and a repeat init once it is known.
// Same marketing-consent gate as the pixel itself: before initOpenAIPixel() ran there is no oaiq.
export function identifyOpenAI(uid?: string, email?: string | null) {
	if (!window.oaiq || uid === identifiedUid) return;
	identifiedUid = uid;
	if (!uid) {
		identity = undefined;
		window.oaiq("init", { user: {} }); // logout: a user was identified, or we would have returned
		return;
	}
	identity = (async () => {
		try {
			const user: Record<string, string> = { external_id_sha256: await sha256Hex(uid) };
			const normalised = email?.trim().toLowerCase();
			if (normalised) user.email_sha256 = await sha256Hex(normalised);
			// the user may have logged out, or been swapped, while hashing
			if (identifiedUid === uid) window.oaiq?.("init", { user });
		} catch {
			// no crypto.subtle (insecure context): attribution is best-effort
		}
	})();
	identityArrived?.();
}

// SSR-safe: trackEvent.ts, its caller, is in the server-rendered marketing graph.
// Both guards sit before any waiting, so without consent this stays a synchronous no-op with no timers.
export const trackOpenAI = async (event: "registration_completed") => {
	if (typeof window === "undefined" || !window.oaiq) return;
	// The signup event fires right after the account is created, usually before App's auth effect
	// has identified the new user. Measure after that init so the conversion carries the user data.
	// shortcut: waits at most 2 s for the identify, a slower auth effect means the signup goes without
	// user data, upgrade by having the signup flow call identifyOpenAI itself.
	if (!identity) {
		await new Promise<void>((resolve) => {
			const timer = setTimeout(resolve, IDENTITY_WAIT_MS);
			identityArrived = () => {
				clearTimeout(timer);
				resolve();
			};
		});
	}
	await identity;
	window.oaiq?.("measure", event, { type: "customer_action" });
};
